import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import { EStartGuideTaskSlug, StartGuideTask } from '@/models/start-guide';
import { IBusinessType } from '@/models/business';

import store from '@/store';

import { app } from '@/main';

import segmentIo from '@/utils/segment.io';

import eventBus from './eventBus';

const START_GUIDE_VIEWED_HIGHLIGHT_KEY =
  '@twism:start_guide_viewed_highlight_key';

export const getTaskReadableName = (taskSlug: string): string => {
  return {
    [EStartGuideTaskSlug.CUSTOMIZE_COIN]: 'Customize Coin',
    [EStartGuideTaskSlug.INVITE_CUSTOMERS]: 'Import Customers',
    [EStartGuideTaskSlug.REFERRAL_PROGRAM]: 'Referral Program',
    [EStartGuideTaskSlug.REWARD_COINS]: 'Reward Program',
    [EStartGuideTaskSlug.SELL_COINS]: 'Selling Coins',
    [EStartGuideTaskSlug.SOCIAL_REWARDS]: 'Social Rewards',
    [EStartGuideTaskSlug.SPECIAL_COINS]: 'Offers',
    [EStartGuideTaskSlug.WELCOME_COINS]: 'Welcome Coins'
  }[
    taskSlug as Exclude<
      EStartGuideTaskSlug,
      EStartGuideTaskSlug.SUCCESS_MESSAGE
    >
  ];
};

export const getStartGuideType = () => {
  const getType = (): string => {
    if (!store.getters['business/getBusinessState'])
      return store.getters['business/getBusinessType'];

    return store.getters['business/getBusinessState'].operationType;
  };

  return getType();
};

export const getReadableStartGuideType = () => {
  return ({
    physical: 'Physical',
    online: 'Online'
  } as { [k: string]: string })[getStartGuideType()];
};

export const needLaunchRewardsProgram = (checkUrls = false, url?: string) => {
  const business = store.getters['business/getBusinessInfo'].business || {};

  let urlsNeedRewards = [
    '/customer-import',
    '/social-rewards',
    '/referral-program',
    '/activation-emails',
    '/coin/loyalty',
    '/coin/policies',
    '/program-status'
  ];

  if (
    getStartGuideType() === IBusinessType.BOTH ||
    getStartGuideType() === IBusinessType.ONLINE
  ) {
    urlsNeedRewards = [...urlsNeedRewards, '/loyalty-widget'];
  }

  if (getStartGuideType() === IBusinessType.ONLINE) {
    urlsNeedRewards = [...urlsNeedRewards, '/offer-free-coins'];
  }

  const isIncludedUrl = urlsNeedRewards.some(item =>
    (url || window.location.href).includes(item)
  );
  const hasRewardConfig =
    business.coinRewardPercentage && business.coinRewardPercentage !== '0.00';

  if (checkUrls) {
    return !hasRewardConfig && isIncludedUrl;
  }

  return !hasRewardConfig;
};

export const shouldAddSuccessStartGuide = (completedTaskSlug: string) => {
  const tasks = store.getters['startGuide/getTasks'] as StartGuideTask[];

  return tasks
    .filter(
      currTask =>
        currTask.slug !== completedTaskSlug && currTask.type === 'task'
    )
    .every(currTask => currTask.completed || currTask.dismissed);
};

export const taskIsComplete = (completedTaskSlug: string) => {
  const tasks = store.getters['startGuide/getTasks'] as StartGuideTask[];

  return tasks.filter(
    currTask => currTask.slug == completedTaskSlug && currTask.completed
  ).length;
};

export const handleCompleteTask = (
  taskSlug: string,
  dismissed = false,
  addSuccessTask = true
) => {
  if (!taskIsComplete(taskSlug)) {
    store.dispatch('startGuide/COMPLETED_TASK_TOGGLE', {
      slug: taskSlug,
      dismissed
    });
  }

  store.dispatch('startGuide/UPDATE_TASKS', taskSlug);

  if (addSuccessTask && shouldAddSuccessStartGuide(taskSlug)) {
    store.dispatch('startGuide/ADD_SUCCESS_TASK');
  }
};

export const handleDispatchStartGuideHighlight = () => {
  const businessId = (store.getters['business/getBusinessInfo'].business || {})
    .id;
  const completedAllTasks = store.getters[
    'startGuide/getCompletedAllTasks'
  ] as boolean;

  const storageData = JSON.parse(
    localStorage.getItem(START_GUIDE_VIEWED_HIGHLIGHT_KEY) || '{}'
  );

  if (storageData[businessId] || completedAllTasks) return;

  eventBus.$emit('startGuideShowHighlight');
  localStorage.setItem(
    START_GUIDE_VIEWED_HIGHLIGHT_KEY,
    JSON.stringify({ ...storageData, [businessId]: true })
  );
};

export const handleTaskTrigger = (task: StartGuideTask, taskProps?: any) => ({
  handleNextTask
}: {
  handleNextTask?: () => void;
}) => {
  const [actionType, actionValue] = (task.trigger || '').split(':');

  if (getTaskReadableName(task.slug)) {
    segmentIo.track('Start Guide Step Started', {
      Feature: getTaskReadableName(task.slug),
      'Start Guide Type': getReadableStartGuideType()
    });
  }

  const actionMapper: Record<string, () => void> = {
    modal: () => {
      window.history.pushState(
        { page_name: app.$route.name },
        '',
        `?modal=${actionValue}`
      );

      eventBus.$emit(
        'openSubModal',
        new ModalOptionsV2({
          icon: 'none',
          hideCancelButton: true,
          confirmEvent: '',
          componentToRender: actionValue,
          mobilePositionBottom: true,
          noBackground: true,
          mobileFullScreen: false,
          disableModalBehaviourMobile: false
        }),
        {
          ...taskProps,
          task,
          onConfirm: () => {
            if (taskIsComplete(task.slug)) {
              handleNextTask && handleNextTask();
            }

            eventBus.$emit('closeModal');
          }
        }
      );
    },
    link: () => {
      app.$router.push({
        name: actionValue,
        params: { ...taskProps, taskSlug: task.slug }
      });
    },
    scrollAt: () => {
      const elementRef = document.getElementById(actionValue);
      if (elementRef) {
        elementRef.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  };

  if (!actionMapper[actionType]) return;

  actionMapper[actionType]();
};
